import React, { useState, useEffect } from "react";
import { NavLink, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Blockquote, Breadcrumb, BreadcrumbHome, BreadcrumbItem, Button, Tabs, Tab, Text, Modal } from "@wfp/ui";
import { iconDeleteGlyph } from '@wfp/icons';
import { ListPage, OrderHistoryData, OrderItem, Role, ServerError, OrderItemChange } from "../../models/types";
import { orderStateParse } from "../../models/validate-order";
import { confirmOrder, getHistory } from "../../restapi/order";
import { discardOrderItems, getOrderItems } from "../../restapi/order-item";
import { useLoggedUser } from "../../hooks/user";
import { useRetrieveOrder } from "../../hooks/order";
import { LoadingCenter } from "../commons/loading-center";
import { OrderItemList } from "../order-item/list";
import { OrderDelivery } from "./delivery";
import { OrderHistory } from "./history";
import { OrderSummary } from "./summary";
import { OrderItemChangeForm } from "../order-item/change-form";
import { useChangeOrderItem, useGetHistory, useRetrieveOrderItems } from "../../hooks/order-item";
import i18n from "../../i18n";
import { StatusBadge } from "../commons/status-badge";
import { DiscardModal } from "./discard-modal";

export const OrderDetail = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { id } = useParams();
    const user = useLoggedUser({});
    const [serverError, setServerError] = useState<ServerError>();
    const [changeError, setChangeError] = useState<string>();
    const [tabIndex, setTabIndex] = useState(parseInt(localStorage.getItem('OrderDetailTabIndex') || '0'));
    const [openChangeOrderItem, setopenChangeOrderItem] = useState<boolean>(false);
    const [orderItemChange, setOrderItemChange] = useState<OrderItem>();
    const [crateObjectChange, setCrateObjectChange] = useState<OrderItemChange>();
    const { mutate, isLoading, isError, error} = useChangeOrderItem();

    const [openDiscardOrderItem, setOpenDiscardOrderItem] = useState(false);
    const [orderItemToDiscard, setOrderItemToDiscard] = useState<OrderItem | null>(null);
    const [discardLoading, setDiscardLoading] = useState(false);
    const [discardError, setDiscardError] = useState<string | null>(null);
    const [selectedOrderItems, setSelectedOrderItems] = useState<number[]>([]);
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

    const order = useRetrieveOrder({ id: Number(id), onError: (e) => setServerError(e) });
    const orderItems = useRetrieveOrderItems({ filters: [{ key: 'order', value: Number(id).toString() }], onError: (e) => setServerError(e) });
    const history = useGetHistory(Number(id), (e) => setServerError(e));

    // Viewport detection - 768px is Tailwind's 'md' breakpoint
    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth < 768);
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    function handleConfirm(): void {
        if (!user) {
            return;
        }
        if (!!order) {
            confirmOrder(order.id).then(() => navigate('/issuance')).catch((serverError) => setServerError(serverError));
        }
    }

    function handleSelectOrderItem() {
        // console.log(id)  TODO: define behavior on selection
    }

    function handleTabChange(idx: number): void {
        localStorage.setItem("OrderDetailTabIndex", idx.toString());
        setTabIndex(idx);
    }

    function handleChangeOrderItem(change: OrderItemChange): void {
        setCrateObjectChange(change);
    }

    function handleOpenModalChangeOrderItem(o: OrderItem): void {
        setOrderItemChange(o);
        setopenChangeOrderItem(true);
    }

    function handleCloseModalChangeOrderItem(): void {
        setopenChangeOrderItem(false);
        setChangeError(undefined);
    }

    function handleSaveChanges(): void {
        if(!!crateObjectChange) {
            mutate(crateObjectChange, {
                onSuccess: () => setopenChangeOrderItem(false),
                onError: (e) => setChangeError(e.message)
            });
        } else {
            handleCloseModalChangeOrderItem()
        }
    }

    function handleDiscardOrderItem(): void {
        // Handle "Select All" and individual selections
        if (selectedOrderItems.length > 0 && orderItems) {
            // Check if "Select All" is selected (special value -1)
            if (selectedOrderItems[0] === -1) {
                setOrderItemToDiscard(null);  // null indicates bulk operation
                setOpenDiscardOrderItem(true);
            } else {
                // Find specific order item
                const itemToDiscard = orderItems.results.find(item => item.id === selectedOrderItems[0]);
                if (itemToDiscard) {
                    setOrderItemToDiscard(itemToDiscard);
                    setOpenDiscardOrderItem(true);
                }
            }
        }
    }

    function handleDiscardOrderItemClose(): void {
        setOpenDiscardOrderItem(false);
        setDiscardError(null);
        setOrderItemToDiscard(null);
    }

    function handleDiscardOrderItemConfirm(reasonId: number, notes: string): void {
        if (!orderItems || !order) return;

        setDiscardLoading(true);
        setDiscardError(null);

        // Convert -1 to undefined (same pattern as print waybills)
        const itemIds = (selectedOrderItems.length === 1 && selectedOrderItems[0] === -1)
            ? undefined  // Discard all items
            : selectedOrderItems;  // Discard specific items

        // Single API call for bulk discard
        discardOrderItems(order.id, itemIds, reasonId, notes)
            .then((result) => {
                if (result.errors && result.errors.length > 0) {
                    // Some items failed but some succeeded
                    setDiscardError(`Discarded ${result.discarded_count}/${result.total_items} items. Errors: ${result.errors.join(', ')}`);
                } else {
                    // All succeeded
                    setDiscardError(null);
                    setOpenDiscardOrderItem(false);
                    setOrderItemToDiscard(null);
                    setSelectedOrderItems([]);
                }
                setDiscardLoading(false);
                // Refresh order items list
                window.location.reload();
            })
            .catch(error => {
                setDiscardError(error?.response?.data?.detail || error?.detail || t('discard_failed'));
                setDiscardLoading(false);
            });
    }

    function canDiscardOrderItem(userRole: Role | undefined): boolean {
        // Only WFP Managers can discard order items
        // Enable when any selection exists (same logic as print waybills button)
        console.log('Selected Order Items:', selectedOrderItems);

        return userRole === Role.WFPManager && selectedOrderItems.length > 0;
    }

    function handleSelectOrderItems(orderItemsId: number[]): void {
        console.log(orderItemsId);
        setSelectedOrderItems(orderItemsId);
    }

    return (
        <div>
            {!!serverError?.errors && (
                <Blockquote kind="error" withIcon>{serverError.errors}</Blockquote>
            )}
            {!!serverError?.detail && (
                <Blockquote kind="error" withIcon>{serverError?.detail?.toString()}</Blockquote>
            )}
            {!!order && (
                <div className="m-10">
                    <Breadcrumb className="mb-5">
                        <BreadcrumbItem>
                            <NavLink to="/"><BreadcrumbHome /></NavLink>
                        </BreadcrumbItem>
                        <BreadcrumbItem>
                            <NavLink to="/orders">{t('order')}</NavLink>
                        </BreadcrumbItem>
                        <BreadcrumbItem disableLink>
                            {t('view_order')}
                        </BreadcrumbItem>
                    </Breadcrumb>
                    <div className="flex justify-between mb-5">
                        <div className={`${i18n.dir() === 'ltr' ? 'ml-0' : 'ml-3'} align-baseline`}>
                            <Text kind='h1'>{t("order_details")}</Text>
                        </div>
                        <div className="flex gap-3">
                            {!!user && user.role === Role.WFPManager && (
                                <Button
                                    className="self-end !inline-flex"
                                    disabled={!canDiscardOrderItem(user?.role)}
                                    onClick={handleDiscardOrderItem}
                                    icon={iconDeleteGlyph}
                                    kind="danger"
                                    iconReverse>
                                    {t('discard_item')}
                                </Button>
                            )}
                            {!!user && user.role === Role.SupplierManager && (
                                <Button className="self-end !inline-flex" disabled={order.state !== 'REQUESTED'} onClick={handleConfirm}>{t('confirm')}</Button>
                            )}
                        </div>
                    </div>
                    <div className="mb-5">
                        <OrderSummary order={order} contentType={order.contentTitle} />
                    </div>
                    <Tabs className='mt-2' selected={tabIndex}>
                        <Tab onClick={() => handleTabChange(0)} label={t('info')} href='' role={'info'} selected={tabIndex === 0} tabIndex={0}>
                            {!!orderItems && (
                                <>
                                    <div className="flex items-center mb-3">
                                        <div>{t("state")}</div>
                                        <div className="ml-5 mr-auto">
                                            <StatusBadge status={order.pendingDiscardRequest ? "DISCARD_REQUESTED" : order.state} />
                                        </div>
                                    </div>
                                    {isMobile ? (
                                        <OrderItemList
                                            contentType={order.contentTitle}
                                            page={orderItems}
                                            mobile={true}
                                            onClickOrderItem={handleOpenModalChangeOrderItem}
                                            onSelectOrderItems={handleSelectOrderItems}
                                        />
                                    ) : (
                                        <OrderItemList
                                            contentType={order.contentTitle}
                                            onClickOrderItem={handleOpenModalChangeOrderItem}
                                            page={orderItems}
                                            mobile={false}
                                            onSelectOrderItems={handleSelectOrderItems}
                                        />
                                    )}
                                </>
                            )}
                        </Tab>
                        <Tab onClick={() => handleTabChange(1)} label={t('history')} href='' role={'history'} selected={tabIndex === 1} tabIndex={1}>
                            {!!history && (
                                <OrderHistory data={history} />
                            )}
                        </Tab>
                        <Tab onClick={() => handleTabChange(2)} label={t('delivery')} href='' role={'delivery'} selected={tabIndex === 2} tabIndex={2}>
                            {!!history && (
                                <OrderDelivery data={history} />
                            )}
                        </Tab>
                    </Tabs>
                </div>
            )}
            {!(serverError || order) && (
                <LoadingCenter />
            )}
            {!!openChangeOrderItem && !!orderItemChange && (
                <Modal
                    open={true}
                    modalHeading={t('school') + ' ' + orderItemChange.schoolRef || ''}
                    modalLabel={t('edit_order') || ''}
                    primaryButtonText={t('save') || ''}
                    onRequestSubmit={handleSaveChanges}
                    onRequestClose={handleCloseModalChangeOrderItem}
                    secondaryButtonText={t('cancel') || ''}
                    primaryButtonDisabled={isLoading}
                >
                    {!!changeError &&(
                        <Blockquote kind="error" withIcon>{changeError}</Blockquote>
                    )}
                    <OrderItemChangeForm onChange={handleChangeOrderItem} orderItem={orderItemChange}></OrderItemChangeForm>
                </Modal>
            )}
            {openDiscardOrderItem && (
                <DiscardModal
                    open={openDiscardOrderItem}
                    onClose={handleDiscardOrderItemClose}
                    onConfirm={handleDiscardOrderItemConfirm}
                    title={t('discard_order_item')}
                    entityName={
                        selectedOrderItems.length === 1 && selectedOrderItems[0] === -1
                            ? `All ${orderItems?.results.length || 0} items`
                            : selectedOrderItems.length === 1 && orderItemToDiscard
                                ? `${orderItemToDiscard.schoolRef} - ${orderItemToDiscard.schoolName}`
                                : `${selectedOrderItems.length} items`
                    }
                    loading={discardLoading}
                    error={discardError}
                />
            )}
        </div>

    );
};
